import { useState, useEffect, useMemo, useCallback, useRef, startTransition } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import { useDeviceMode } from './useDeviceMode';
import { useTracking } from './useTracking';
import { getDealerCoords, getDefaultItemsPerPage } from '../utils/itemListHelpers';
import { ADMIN_LECCE_COORDS } from '../config/filters';
import { geoCacheReady, getCachedGeoSettings } from '../config/geoConfig';

export function useItemListFilters(itemType: 'boats' | 'motors' | 'accessories') {
  const { user } = useAuth();
  const { isDesktop } = useDeviceMode();
  const { trackFilter, trackSearch } = useTracking();
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  const dealerCoords = useMemo(() => getDealerCoords(user), [user?.lat, user?.lng]);
  
  // Admin coords from auth response (admin_lat/admin_lng) or settings fetch
  const [adminCoords, setAdminCoords] = useState<{lat: number, lng: number} | null>(() => {
    const cached = getCachedGeoSettings();
    if (cached) return { lat: cached.admin_lat, lng: cached.admin_lng };
    const u = user as any;
    if (u?.admin_lat && u?.admin_lng) {
      return { lat: Number(u.admin_lat), lng: Number(u.admin_lng) };
    }
    return null;
  });
  const [adminCoordsLoaded, setAdminCoordsLoaded] = useState(() => {
    if ((user as any)?.is_admin) return true;
    return geoCacheReady();
  });
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [columns, setColumns] = useState<2 | 3 | 4>(4);
  
  // Pagination state (desktop only)
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Price filter tracking
  const [userAppliedPriceFilter, setUserAppliedPriceFilter] = useState(false);
  
  // Search state
  const [quickSearch, setQuickSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortKey, setSortKey] = useState(0);
  
  // Filter state
  const getInitialFilter = (key: string) => searchParams.get(key) || '';
  const getInitialPriceFilter = (key: string) => '';
  
  const [filters, setFilters] = useState({
    brand: getInitialFilter('brand'),
    model: getInitialFilter('model'),
    min_price: getInitialPriceFilter('min_price'),
    max_price: getInitialPriceFilter('max_price'),
    year: getInitialFilter('year'),
    min_length: getInitialFilter('min_length'),
    max_length: getInitialFilter('max_length'),
    engine_type: getInitialFilter('engine_type'),
    fuel_type: getInitialFilter('fuel_type'),
    condition: getInitialFilter('condition'),
    min_hours: getInitialFilter('min_hours'),
    max_hours: getInitialFilter('max_hours'),
    horsepower: getInitialFilter('horsepower'),
    boat_type_code: getInitialFilter('boat_type_code'),
  });
  
  // Location state
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    searchParams.get('lat') && searchParams.get('lng') 
      ? { lat: parseFloat(searchParams.get('lat')!), lng: parseFloat(searchParams.get('lng')!) } 
      : null
  );
  const [locationStatus, setLocationStatus] = useState('');
  
  const [radius, setRadius] = useState<number>(() => {
    const r = searchParams.get('radius');
    return r ? parseInt(r) : -1;
  });
  
  const [badgePerPage, setBadgePerPage] = useState<number | null>(null);
  
  const [useLocation, setUseLocation] = useState(searchParams.get('use_gps') === 'true');
  const [useFixedLocation, setUseFixedLocation] = useState(true);
  
  const [availableOnly, setAvailableOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    const status = searchParams.get('status');
    return status || '';
  });
  const [sortBy, setSortBy] = useState(() => {
    const fromUrl = searchParams.get('sort');
    if (fromUrl) return fromUrl;
    return 'distance_asc';
  });

  const [prevFiltersKey, setPrevFiltersKey] = useState<string>('');

  // Track automatic radius init (from radius-steps API)
  // Skip URL sync during auto-init to prevent full-page re-render from setSearchParams
  const radiusAutoInitRef = useRef(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(quickSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [quickSearch]);
  
  // Reset page on column change
  useEffect(() => {
    if (isDesktop) {
      setPage(1);
      setItemsPerPage(getDefaultItemsPerPage(columns));
    }
  }, [columns, isDesktop]);
  
  // Reset page on filter change
  useEffect(() => {
    const filtersKey = JSON.stringify({ 
      ...filters, 
      debouncedSearch, 
      radius, 
      availableOnly, 
      statusFilter,
      sortBy 
    });
    if (prevFiltersKey && prevFiltersKey !== filtersKey) {
      setPage(1);
    }
    setPrevFiltersKey(filtersKey);
  }, [filters, debouncedSearch, radius, availableOnly, statusFilter, sortBy, isDesktop, prevFiltersKey]);
  
  // URL sync — skip during automatic radius initialization (prevents full-page re-render)
  useEffect(() => {
    if (radius >= 0 && !radiusAutoInitRef.current) {
      radiusAutoInitRef.current = true;
      return;
    }

    const timer = setTimeout(() => {
      const params: Record<string, string> = {};
      if (filters.brand) params.brand = filters.brand;
      if (filters.model) params.model = filters.model;
      if (userAppliedPriceFilter && filters.min_price) params.min_price = filters.min_price;
      if (userAppliedPriceFilter && filters.max_price) params.max_price = filters.max_price;
      if (filters.year) params.year = filters.year;
      if (filters.min_length) params.min_length = filters.min_length;
      if (filters.max_length) params.max_length = filters.max_length;
      if (filters.engine_type) params.engine_type = filters.engine_type;
      if (filters.fuel_type) params.fuel_type = filters.fuel_type;
      if (filters.condition) params.condition = filters.condition;
      if (filters.boat_type_code) params.boat_type_code = filters.boat_type_code;
      
      const activeLoc = getActiveLocation();
      if (activeLoc) {
        params.lat = activeLoc.lat.toString();
        params.lng = activeLoc.lng.toString();
      }
      if (useLocation) params.use_gps = 'true';
      if (useFixedLocation) params.fixed_location = 'true';
      if (availableOnly) params.available_only = 'true';
      if (statusFilter) params.status = statusFilter;
      if (sortBy !== 'created_desc') params.sort = sortBy;
      if (radius !== -1) params.radius = radius.toString();
      
      startTransition(() => {
        setSearchParams(params, { replace: true });
      });
    }, 300);
    
    return () => clearTimeout(timer);
  }, [filters, location, useLocation, useFixedLocation, availableOnly, statusFilter, sortBy, radius, setSearchParams, userAppliedPriceFilter, debouncedSearch]);
  
  // Helpers
  const getDealerLocation = () => {
    if (dealerCoords && dealerCoords.lat != null && dealerCoords.lng != null && dealerCoords.lat !== 0 && dealerCoords.lng !== 0) {
      return { lat: dealerCoords.lat, lng: dealerCoords.lng };
    }
    return null;
  };
  
  const getDealerCity = () => {
    if (user?.city) return user.city;
    if ((user as any)?.citta) return (user as any).citta;
    return null;
  };
  
  const getActiveLocation = () => {
    const isAdmin = (user as any)?.is_admin === true;
    const dealerLoc = getDealerLocation();
    const dealerCity = getDealerCity();
    
    if (dealerLoc) {
      const locationName = dealerCity || `${(Number(dealerLoc.lat) || 0).toFixed(4)},${(Number(dealerLoc.lng) || 0).toFixed(4)}`;
      return { lat: dealerLoc.lat, lng: dealerLoc.lng, source: `Mio Negozio: ${locationName}` };
    }
    
    if (isAdmin && adminCoords) {
      return { lat: adminCoords.lat, lng: adminCoords.lng, source: 'Dal Settings (Admin)' };
    }
    if (isAdmin) {
      return { lat: ADMIN_LECCE_COORDS.lat, lng: ADMIN_LECCE_COORDS.lng, source: 'Dal Settings (Admin)' };
    }
    
    if (useLocation && location && location.lat && location.lng) {
      return { lat: location.lat, lng: location.lng, source: 'GPS' };
    }
    if (location && location.lat && location.lng) {
      return { lat: location.lat, lng: location.lng, source: 'Selezionata' };
    }
    
    if (useFixedLocation) {
      return { lat: ADMIN_LECCE_COORDS.lat, lng: ADMIN_LECCE_COORDS.lng, source: 'Lecce' };
    }
    
    return { lat: ADMIN_LECCE_COORDS.lat, lng: ADMIN_LECCE_COORDS.lng, source: 'Default' };
  };
  
  const activeLocation = useMemo(() => getActiveLocation(), [
    dealerCoords?.lat, dealerCoords?.lng,
    (user as any)?.is_admin, adminCoords?.lat, adminCoords?.lng,
    useLocation, location?.lat, location?.lng, useFixedLocation,
  ]);
  
  // Build activeFilters
  const activeFilters = useMemo(() => {
    const baseFilters: Record<string, any> = {
      ...filters,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(radius >= 0 ? { lat: activeLocation?.lat, lng: activeLocation?.lng, radius_km: radius } : { radius_km: -1 }),
      ...(availableOnly ? { available_only: 'true' } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      sort_by: sortBy,
    };
    
    Object.keys(baseFilters).forEach(key => {
      const val = baseFilters[key];
      if (val === '' || val === null || val === undefined) {
        delete baseFilters[key];
      }
    });
    
    const effectivePerPage = badgePerPage !== null ? badgePerPage : itemsPerPage;
    
    return {
      ...baseFilters,
      page: page,
      per_page: effectivePerPage,
    };
  }, [filters, debouncedSearch, activeLocation, radius, availableOnly, statusFilter, sortBy, isDesktop, page, itemsPerPage, badgePerPage]);
  
  // Handlers
  const hasSearchResetFilters = useRef(false);
  
  const handleSearchChange = useCallback((value: string) => {
    setQuickSearch(value);
    if (value.trim() !== '' && !hasSearchResetFilters.current) {
      hasSearchResetFilters.current = true;
      setFilters({
        brand: '', model: '', min_price: '', max_price: '',
        year: '', min_length: '', max_length: '',
        engine_type: '', fuel_type: '', condition: '',
        min_hours: '', max_hours: '', horsepower: '', boat_type_code: '',
      });
      setLocation(null);
      setLocationStatus('');
      setRadius(-1);
      setUseLocation(false);
      setUseFixedLocation(false);
      setAvailableOnly(false);
    } else if (value.trim() === '') {
      hasSearchResetFilters.current = false;
    }
    
    if (value.trim() !== '') {
      trackSearch(value.trim(), filters, undefined).catch(console.error);
    }
  }, [filters, trackSearch]);
  
  const handleResetAll = useCallback(() => {
    trackFilter('reset_all', 'all', 'filter_reset').catch(console.error);
    
    setFilters({
      brand: '', model: '', min_price: '', max_price: '',
      year: '', min_length: '', max_length: '',
      engine_type: '', fuel_type: '', condition: '',
      min_hours: '', max_hours: '', horsepower: '', boat_type_code: '',
    });
    setLocation(null);
    setLocationStatus('');
    setRadius(-1);
    setUseLocation(false);
    setUseFixedLocation(false);
    setAvailableOnly(false);
    setStatusFilter('');
    setQuickSearch('');
    setSortBy('created_desc');
    startTransition(() => {
      setSearchParams({}, { replace: true });
    });
  }, [trackFilter, setSearchParams]);
  
  const handleSortChange = useCallback((newSort: string) => {
    setSortBy(newSort);
    setSortKey(prev => prev + 1);
  }, []);
  
  const handlePriceFilterChange = useCallback((min: string, max: string) => {
    setUserAppliedPriceFilter(true);
    setFilters(prev => ({ ...prev, min_price: min, max_price: max }));
  }, []);
  
  const setRadiusFromBadge = useCallback((km: number, count?: number) => {
    setRadius(km);
    setUseFixedLocation(true);
    setUseLocation(false);
    setPage(1);
    setBadgePerPage(null);
  }, []);
  
  // Helpers
  const hasActiveFilters = useCallback(() => {
    return (
      filters.brand !== '' || filters.model !== '' ||
      filters.min_price !== '' || filters.max_price !== '' ||
      filters.year !== '' || filters.min_length !== '' ||
      filters.max_length !== '' || filters.engine_type !== '' ||
      filters.fuel_type !== '' || filters.condition !== '' ||
      filters.min_hours !== '' || filters.max_hours !== '' || filters.horsepower !== '' ||
      useLocation || useFixedLocation || location !== null || availableOnly || statusFilter !== ''
    );
  }, [filters, useLocation, useFixedLocation, location, availableOnly, statusFilter]);
  
  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (filters.brand) count++;
    if (filters.model) count++;
    if (filters.min_price) count++;
    if (filters.max_price) count++;
    if (filters.year) count++;
    if (filters.min_length) count++;
    if (filters.max_length) count++;
    if (filters.engine_type) count++;
    if (filters.fuel_type) count++;
    if (filters.condition) count++;
    if (filters.min_hours) count++;
    if (filters.max_hours) count++;
    if (filters.horsepower) count++;
    if (useLocation) count++;
    if (useFixedLocation) count++;
    if (location) count++;
    if (availableOnly) count++;
    if (statusFilter) count++;
    return count;
  }, [filters, useLocation, useFixedLocation, location, availableOnly, statusFilter]);
  
  return {
    // States
    filters,
    setFilters,
    location,
    setLocation,
    adminCoordsLoaded,
    locationStatus,
    setLocationStatus,
    radius,
    setRadius,
    useLocation,
    setUseLocation,
    useFixedLocation,
    setUseFixedLocation,
    availableOnly,
    setAvailableOnly,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    quickSearch,
    setQuickSearch,
    debouncedSearch,
    page,
    setPage,
    itemsPerPage,
    setItemsPerPage,
    totalItems,
    setTotalItems,
    totalPages,
    setTotalPages,
    columns,
    setColumns,
    sidebarOpen,
    setSidebarOpen,
    showFilters,
    setShowFilters,
    sortKey,
    setSortKey,
    badgePerPage,
    setBadgePerPage,
    userAppliedPriceFilter,
    setUserAppliedPriceFilter,
    
    // Derived
    activeFilters,
    activeLocation,
    hasActiveFilters,
    getActiveFiltersCount,
    getDealerCoords: () => dealerCoords,
    getDealerCity,
    getDefaultItemsPerPage,
    
    // Handlers
    handleSearchChange,
    handleResetAll,
    handleSortChange,
    handlePriceFilterChange,
    setRadiusFromBadge,
    
    ADMIN_LECCE_COORDS,
  };
}
